<template>
  <section>
    <div
      class="tw:flex tw:flex-col tw:lg:flex-row tw:gap-5 tw:md:gap-8 tw:w-full tw:lg:max-w-(--tw-breakpoint-landing) tw:md:max-w-[638px] tw:py-6 tw:lg:px-0 tw:mx-auto tw:lg:py-8"
    >
      <div v-if="isVideoMode" class="tw:relative tw:w-[70%] tw:md:w-[600px] tw:lg:w-[700px] tw:mx-auto">
        <Videoplayer
          :src="StudioPlusVideo"
          :isRounded="false"
          :controls="true"
          :muted="true"
          :autoplay="true"
          :playOnScroll="false"
          :data-testid="'plus-video-landing'"
        />
        <button
          @click="handleCloseVideo"
          class="tw:absolute tw:left-[calc(100%+32px)] tw:top-0"
          :aria-label="$t('close')"
          :data-testid="'plus-video-landing-close-btn'"
        >
          <Close class="tw:size-3.5" />
        </button>
      </div>

      <!-- Web Version -->
      <div
        v-show="!isVideoMode"
        class="tw:hidden tw:order-1 tw:lg:order-0 tw:lg:w-1/2 tw:md:flex tw:flex-wrap tw:gap-4 tw:lg:gap-6"
      >
        <div
          v-for="item in STUDIO_PLUS_BENEFITS"
          :key="item.id"
          :ref="el => registerElement(el)"
          class="tw:lg:ml-[30px] tw:relative shadow tw:bg-white tw:last:mb-0 tw:rounded-xl tw:px-6 tw:py-5 tw:border tw:border-gray-300 tw:lg:pl-12 tw:flex tw:flex-row tw:items-center tw:w-full"
        >
          <div
            class="tw:lg:absolute tw:bg-decorative-orange tw:min-w-[45px] tw:h-[45px] tw:lg:h-[60px] tw:lg:w-[60px] tw:flex tw:justify-center tw:items-center tw:rounded-full icon tw:mr-4 tw:lg:mr-0"
          >
            <img :src="item.icon" :alt="$t(item.title)" />
          </div>
          <div>
            <h2 class="tw:font-bold tw:lg:text-xl">{{ $t(item.title) }}</h2>
            <p class="tw:text-sm tw:lg:text-base tw:mt-2 tw:lg:mt-0">{{ $t(item.text) }}</p>
          </div>
        </div>
      </div>

      <div
        v-if="!isVideoMode || (screenWidth <= 768 && !isVideoMode)"
        class="tw:order-0 tw:lg:order-1 tw:lg:w-1/2 tw:flex tw:flex-col tw:md:flex-row tw:lg:flex-col tw:items-center tw:lg:items-stretch tw:mx-2.5 tw:xs:mx-6 tw:md:mx-0"
      >
        <div class="tw:order-0 tw:md:order-1 tw:lg:order-0 tw:w-full tw:md:w-1/2 tw:lg:w-auto tw:md:ml-8 tw:lg:ml-0">
          <img :src="StudioPlusLogo" class="tw:lg:w-[280px] tw:md:w-[216px] tw:w-[183px] tw:mx-auto tw:md:mx-0" />
          <h3
            class="tw:font-bold tw:lg:text-[30px] tw:lg:leading-[40px] tw:md:text-2xl tw:text-xl tw:mt-2 tw:md:mt-4 tw:mb-4 tw:md:mb-0 tw:text-center tw:md:text-left"
            v-html="$t('plus_product_page_header')"
          />
        </div>

        <div
          class="tw:relative tw:order-0 tw:lg:order-1 tw:rounded-xl tw:border tw:border-black line-shadow tw:lg:mt-6 tw:grow tw:flex tw:items-center tw:justify-center tw:h-[170px] tw:md:h-[166px] tw:lg:h-[270px] tw:md:w-1/2 tw:lg:w-full tw:w-full tw:overflow-hidden"
          :class="{ 'tw:cursor-pointer': language.includes('en') }"
          @click="handlePlayClick"
          data-testid="studio-plus-play-trigger"
        >
          <div class="businessStudioBg tw:absolute tw:inset-0" :class="{ 'tw:opacity-20': language.includes('en') }" />

          <img
            v-if="language.includes('en')"
            :src="PlayButton"
            :alt="$t('play')"
            class="tw:relative tw:z-10 tw:transition-transform hover:tw:scale-110"
          />
        </div>
      </div>

      <!-- Mobile Version -->
      <div
        ref="containerRef"
        class="tw:md:hidden tw:flex tw:flex-col tw:md:flex-row tw:w-vw tw:overflow-hidden carousel-container tw:ml-2.5 tw:xs:ml-6"
      >
        <div class="inner plus_carousel-mobile" ref="inner" :style="innerStyles">
          <div
            ref="boxesStudioRef"
            v-for="item in STUDIO_PLUS_BENEFITS"
            :key="item.id"
            :id="item.id"
            class="card shadow tw:bg-white tw:rounded-xl tw:px-6 tw:py-5 tw:border tw:border-gray-300"
          >
            <div class="tw:h-full">
              <div class="tw:flex tw:items-center">
                <div
                  class="tw:bg-decorative-orange tw:min-w-[45px] tw:w-[45px] tw:h-[45px] tw:flex tw:justify-center tw:items-center tw:rounded-full tw:mr-4"
                >
                  <img :src="item.icon" :alt="$t(item.title)" />
                </div>
                <h2 class="tw:font-bold">{{ $t(item.title) }}</h2>
              </div>
              <p class="tw:text-sm tw:mt-2">{{ $t(item?.text) }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
<script setup>
import { onMounted, onBeforeUnmount, ref, watch, nextTick, onUpdated } from 'vue'

import TouchEvent from '@/utils/touchEvents.ts'
import Videoplayer from '@/components/studioPlus/Videoplayer.vue'
import Close from '@/components/icons/Close.svg?component'

import { useSlideInAnimation } from '@/composables/useScrollSlideIn.ts'
import { useIsMobile } from '@/composables/useIsMobile'
import { useSessionValues } from '@/composables/useSessionValues'

import StudioPlusLogo from '@/assets/img/landing/landing_studio_plus.svg'
import StudioPlusVideo from '@/assets/videos/HomePageBusinessStudioPlus.mp4'
import HotelConnect from '@/assets/img/landing/icons/hotel_connect_icon.svg'
import RateInsights from '@/assets/img/landing/icons/rate_insights_icon.svg'
import VisitorsProfile from '@/assets/img/landing/icons/visitors_profile_icon.svg'
import HotelOffers from '@/assets/img/landing/icons/hotel_offers_icon.svg'
import PlayButton from '@/assets/img/studio-plus/icons/PlayIcon.svg'

const { registerElement } = useSlideInAnimation({
  threshold: 0.3,
  staggerDelay: 500,
  direction: 'left-to-right',
})

const STUDIO_PLUS_BENEFITS = [
  { id: 0, title: 'tbs_mp_plus_benefits_1', text: 'tbs_mp_plus_benefits_1_text', icon: HotelConnect },
  { id: 1, title: 'tbs_mp_plus_benefits_2', text: 'tbs_mp_plus_benefits_2_text', icon: VisitorsProfile },
  { id: 2, title: 'tbs_mp_plus_benefits_3', text: 'tbs_mp_plus_benefits_3_text', icon: RateInsights },
  { id: 3, title: 'hotel_offers_product_header', text: 'tbs_mp_plus_benefits_4_text', icon: HotelOffers },
]

const { screenWidth } = useIsMobile()
const isVideoMode = ref(false)
const containerRef = ref(null)
const boxesStudioRef = ref([])
const { language } = useSessionValues()

const touchEvent = ref(null)
const current = ref(1)
const timer = ref('')
const isAutoplayActive = ref(true)
const inner = ref(null)
const innerStyles = ref({})
const step = ref('')
const transitioning = ref(false)

const setEqualHeight = () => {
  nextTick(async () => {
    if (window.innerWidth > 1024) {
      boxesStudioRef.value.forEach(box => (box.style.height = 'auto'))
      return
    }
    const boxes = boxesStudioRef.value
    if (!boxes || boxes.length === 0) return

    boxes.forEach(box => {
      box.style.height = 'auto'
    })

    await nextTick()

    let maxHeight = 0

    boxes.forEach(box => {
      const currentHeight = box.offsetHeight

      if (currentHeight > maxHeight) {
        maxHeight = currentHeight
      }
    })

    boxes.forEach(box => {
      box.style.height = maxHeight + 'px'
    })
  })
}

watch(language, () => {
  nextTick(() => setEqualHeight())
})

onUpdated(() => {
  setEqualHeight()
})

onMounted(async () => {
  await nextTick()
  setStep()
  autoplay()
  document.addEventListener('touchstart', event => {
    const carousel = containerRef.value?.querySelector('.plus_carousel-mobile')
    if (carousel && carousel.contains(event.target)) {
      touchEvent.value = new TouchEvent(event)
    } else {
      touchEvent.value = null
    }
  })

  document.addEventListener('touchend', handleSwipe)
  window.addEventListener('resize', setEqualHeight)
})

onBeforeUnmount(() => {
  clearTimeout(timer.value)
  window.removeEventListener('resize', setEqualHeight)
  document.removeEventListener('touchend', handleSwipe)
})

const afterTransition = callback => {
  const listener = () => {
    callback()
    inner.value?.removeEventListener('transitionend', listener)
  }
  inner.value?.addEventListener('transitionend', listener)
}

const resetTranslate = () => {
  innerStyles.value = {
    transition: 'none',
    transform: 'translateX(0)',
  }
}

const setStep = () => {
  const innerWidth = inner.value.scrollWidth
  const totalCards = STUDIO_PLUS_BENEFITS.length

  step.value = innerWidth / totalCards
}

const moveLeft = () => {
  innerStyles.value = {
    transition: 'transform 0.5s ease',
    transform: `translateX(-${step.value}px)`,
  }
}

const moveRight = () => {
  innerStyles.value = {
    transition: 'transform 0.5s ease',
    transform: `translateX(${step.value}px)`,
  }
}

const next = () => {
  if (transitioning.value) return
  transitioning.value = true
  moveLeft()
  afterTransition(() => {
    const firstElement = containerRef.value.querySelector('.plus_carousel-mobile')?.firstElementChild
    containerRef.value.querySelector('.plus_carousel-mobile').appendChild(firstElement)

    updateCurrent()
    resetTranslate()
    transitioning.value = false
  })
}

const prev = () => {
  if (transitioning.value) return
  transitioning.value = true
  moveRight()
  afterTransition(() => {
    const lastElement = containerRef.value.querySelector('.plus_carousel-mobile').lastElementChild
    containerRef.value.querySelector('.plus_carousel-mobile').prepend(lastElement)

    updateCurrent()
    resetTranslate()
    transitioning.value = false
  })
}

const updateCurrent = () => {
  const firstElementId = containerRef.value.querySelector('.plus_carousel-mobile').firstElementChild.id
  current.value = Number(firstElementId.split('_')[1])
}

const autoplay = () => {
  if (!isAutoplayActive.value) return
  next()
  timer.value = setTimeout(autoplay, 6000)
}

const handleSwipe = event => {
  if (!touchEvent.value) {
    return
  }
  isAutoplayActive.value = false
  clearTimeout(timer.value)
  touchEvent.value.setEndEvent(event)

  if (touchEvent.value.isSwipeRight()) {
    prev()
  } else if (touchEvent.value.isSwipeLeft()) {
    next()
  }
  touchEvent.value = null
}
const handlePlayClick = () => {
  if (language.value.includes('en')) {
    isVideoMode.value = true
  }
}
const handleCloseVideo = () => {
  isVideoMode.value = false
}
</script>
<style scoped>
.line-shadow {
  box-shadow: 3px 3px 0px 0px #000;
}
.shadow {
  box-shadow: 2px 2px 4px 0px #00000040;
}
.icon {
  @media (min-width: 1024px) {
    left: -30px;
    top: 50%;
    transform: translateY(-50%);
  }
}
.image-column {
  flex: 0 0 auto;
}
.plus_carousel-mobile {
  padding-bottom: 2px;
  display: flex;
  margin: auto;
  white-space: nowrap;
  transition: transform 1s;
  @media (max-width: 768px) {
    width: 100%;
  }
}

.card {
  background-color: var(--tw-color-white);
  min-width: 300px;
  min-height: 100%;
  position: relative;
  width: 330px;
  margin-right: 18px;
  display: inline-flex;
  white-space: wrap;
}

.businessStudioBg {
  background-image: url(../../assets/img/landing/landing_studio_plus_img.jpeg);
  background-size: cover;
  background-position: center;
}
</style>
